
import { useState } from 'react';
import { CircleAlert, Upload } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardHead,
  Field,
  FilePicker,
  Input,
  SearchableSelect,
} from '../../components/ui';
import { useCreateVoice } from '../../hooks';
import { LANGUAGES } from '../../lib/languages';
import type { Id } from '../../types/api';
import type { TtsProvider } from '../../types/api';
import styles from './VoicesPage.module.css';

interface Props {
  onClose: () => void;
  onClone: (voiceId: Id) => void;
  cloneBusy: boolean;
  provider: TtsProvider;
}

export function CreateVoiceForm({ onClose, onClone, cloneBusy, provider }: Props) {
  const create = useCreateVoice();

  const [name, setName] = useState('');
  const [language, setLanguage] = useState('ru');
  const [file, setFile] = useState<File | null>(null);

  const busy = create.isPending || cloneBusy;
  const canSubmit = Boolean(file) && Boolean(language) && !busy;

  const submit = () => {
    if (!canSubmit || !file) return;
    create.mutate(
      { language, name: name.trim() || undefined, sample: file, provider },
      {
        onSuccess: (voice) => {
          if (!voice.external_voice_id) onClone(voice.id);
          onClose();
        },
      },
    );
  };

  return (
    <Card className={styles.panelAccent}>
      <CardHead
        title={
          <span className={styles.headTitle}>
            <Upload size={14} strokeWidth={1.5} />
            Новый голос
          </span>
        }
        actions={
          <Button size="sm" onClick={onClose}>
            Свернуть
          </Button>
        }
      />

      <div className={styles.panelBody}>
        <div className={styles.notice}>
          <CircleAlert size={14} strokeWidth={1.5} className={styles.noticeIcon} />
          <span>
            При клонировании голоса необходимо использовать только кусок аудио без междометий и
            долгих пауз в речи, иначе после клонирования и озвучки будут междометия и паузы.
          </span>
        </div>

        <div className={styles.formGrid}>
          <Field
            layout="stacked"
            label="Запись голоса"
            hint="Минимальная длина загружаемого аудио для клонирования 10 секунд"
          >
            <FilePicker
              file={file}
              onPick={setFile}
              accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
              disabled={busy}
              emptyLabel="файл не выбран"
              ariaLabel="Запись голоса"
            />
          </Field>

          <Field layout="stacked" label="Язык">
            {() => (
              <SearchableSelect
                fullWidth
                options={LANGUAGES.map((item) => ({ value: item.code, label: item.label }))}
                value={language}
                onChange={(next) => setLanguage(next ?? language)}
                disabled={busy}
                ariaLabel="Язык голоса"
              />
            )}
          </Field>

          <Field layout="stacked" label="Название">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="необязательно"
                disabled={busy}
              />
            )}
          </Field>
        </div>

        {create.error && <ErrorBanner error={create.error} />}

        <div className={styles.actions}>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Создаём…' : 'Создать голос'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
